import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import { download, ExportButton, fileName, ledgerSheet, rangeLabel } from "./excel";
import CustomerSelect, { customerLabel } from "../CustomerSelect";
import { escapeHtml, printHtml, tableHtml } from "../../utils/printTable";

const LEDGER_COLUMNS = [
  { key: "date", label: "Date" },
  { key: "particulars", label: "Particulars" },
  { key: "vchType", label: "Vch Type" },
  { key: "vchNo", label: "Vch No" },
  { key: "debit", label: "Debit", format: "money" },
  { key: "credit", label: "Credit", format: "money" },
];

const money = (value) => (value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

const LedgerStatementReport = () => {
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(false);
  // the customer and dates the statement on screen was loaded for
  const [applied, setApplied] = useState(null);

  const exportExcel = () => {
    const filters = rangeLabel(applied.fromDate, applied.toDate);
    download(fileName(`Ledger ${applied.customerName}`, filters), [
      { ...ledgerSheet(`Ledger Statement — ${applied.customerName}`, filters, statement), name: "Ledger Statement" },
    ]);
  };

  useEffect(() => {
    api.getAll("/api/v1/customers").then((data) => setCustomers(data?.content || [])).catch(() => {});
  }, []);

  const load = async () => {
    if (!customerId) {
      toast.error("Select a customer");
      return;
    }
    setLoading(true);
    try {
      const data = await api.get("/api/v1/reports/ledger-statement", {
        customerId: Number(customerId),
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      });
      setStatement(data);
      setApplied({ customerName: customerLabel(customers.find((c) => String(c.id) === String(customerId))) || "Customer", fromDate, toDate });
    } catch (error) {
      toast.error(error.message || "Failed to load ledger statement");
    } finally {
      setLoading(false);
    }
  };

  const rows = statement?.entries || [];

  /** The statement in the print window — "Save as PDF" there gives the PDF. */
  const printPdf = () => {
    const summary = `<table style="width:50%;margin-left:auto"><tbody>
      <tr><th style="text-align:left">Opening Balance</th><td class="r">${escapeHtml(money(statement.openingBalance))}</td></tr>
      <tr><th style="text-align:left">Total Debit</th><td class="r">${escapeHtml(money(statement.totalDebit))}</td></tr>
      <tr><th style="text-align:left">Total Credit</th><td class="r">${escapeHtml(money(statement.totalCredit))}</td></tr>
      <tr><th style="text-align:left">Closing Balance</th><td class="r"><b>${escapeHtml(money(statement.closingBalance))}</b></td></tr>
    </tbody></table>`;
    printHtml(`Ledger Statement — ${applied.customerName}`, rangeLabel(applied.fromDate, applied.toDate),
      `${tableHtml(LEDGER_COLUMNS, rows, { totals: true })}${summary}`, { landscape: false });
  };

  return (
    <div className="p-4" style={{ position: "relative" }}>
      {loading ? (
        <div className="loading-overlay">
          <div className="spinner-card">
            <div className="spinner-ring" />
            <div className="spinner-label">Loading ledger statement...</div>
          </div>
        </div>
      ) : null}

      <div className="page-subtitle" style={{ marginBottom: "12px" }}>
        A customer's running account — sales invoices, receipts, and credit/debit notes in date order, with an
        opening and closing balance.
      </div>

      <div className="filters-bar">
        <CustomerSelect customers={customers} value={customerId} onChange={setCustomerId} style={{ flex: "1 1 640px" }} />
        <input type="date" onClick={(e) => e.target.showPicker?.()} value={fromDate} onChange={(e) => setFromDate(e.target.value)} aria-label="From date" />
        <input type="date" onClick={(e) => e.target.showPicker?.()} value={toDate} onChange={(e) => setToDate(e.target.value)} aria-label="To date" />
        <button className="primary" onClick={load} disabled={loading}>
          {loading ? "Loading..." : "Load"}
        </button>
        <ExportButton onClick={exportExcel} disabled={loading || !statement} />
        <button className="secondary" onClick={printPdf} disabled={loading || !statement} title="Print, or save as PDF from the print dialog">
          Print / PDF
        </button>
      </div>

      {statement ? (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Particulars</th>
                <th>Vch Type</th>
                <th>Vch No</th>
                <th>Debit</th>
                <th>Credit</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <div className="empty-state">
                      <span className="empty-state-title">No transactions in this period</span>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={index}>
                    <td>{row.date}</td>
                    <td>{row.particulars}</td>
                    <td>{row.vchType}</td>
                    <td>{row.vchNo}</td>
                    <td>{row.debit && Number(row.debit) !== 0 ? money(row.debit) : ""}</td>
                    <td>{row.credit && Number(row.credit) !== 0 ? money(row.credit) : ""}</td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} style={{ textAlign: "right", fontWeight: 600 }}>
                  Opening Balance:
                </td>
                <td colSpan={2} style={{ fontWeight: 600 }}>{money(statement.openingBalance)}</td>
              </tr>
              <tr>
                <td colSpan={4} style={{ textAlign: "right", fontWeight: 600 }}>
                  Current Total:
                </td>
                <td style={{ fontWeight: 600 }}>{money(statement.totalDebit)}</td>
                <td style={{ fontWeight: 600 }}>{money(statement.totalCredit)}</td>
              </tr>
              <tr>
                <td colSpan={4} style={{ textAlign: "right", fontWeight: 700 }}>
                  Closing Balance:
                </td>
                <td colSpan={2} style={{ fontWeight: 700 }}>{money(statement.closingBalance)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      ) : null}
    </div>
  );
};

export default LedgerStatementReport;
